'use client';

import React from 'react';
import { FilterState } from './ApartmentsListingContent';
import { SlidersHorizontal, RotateCcw } from 'lucide-react';

interface ApartmentFiltersProps {
  filters: FilterState;
  onFilterChange: (f: FilterState) => void;
  total: number;
}

const apartmentTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'studio', label: 'Studio' },
  { value: '1bed', label: '1 Bedroom' },
  { value: '2bed', label: '2 Bedrooms' },
  { value: '3bed', label: '3 Bedrooms' },
];

const guestOptions = [
  { value: 0, label: 'Any' },
  { value: 1, label: '1+' },
  { value: 2, label: '2+' },
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 6, label: '6+' },
];

export default function ApartmentFilters({ filters, onFilterChange, total }: ApartmentFiltersProps) {
  const update = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    onFilterChange({ ...filters, [key]: value });

  const reset = () =>
    onFilterChange({ search: '', type: 'all', minPrice: 0, maxPrice: 500000, guests: 0, availableOnly: false, sortBy: 'featured' });

  return (
    <div className="card-base p-5 sticky top-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-primary" />
          <h3 className="font-bold text-sm text-foreground">Filters</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{total} results</span>
          <button
            onClick={reset}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            title="Reset all filters"
          >
            <RotateCcw size={13} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Search</label>
        <input
          type="text"
          placeholder="Apartment name or location…"
          value={filters.search}
          onChange={(e) => update('search', e.target.value)}
          className="input-field text-sm"
        />
      </div>

      {/* Type */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Apartment Type</label>
        <div className="flex flex-col gap-1.5">
          {apartmentTypes.map((t) => (
            <button
              key={`type-${t.value}`}
              onClick={() => update('type', t.value)}
              className={`px-3 py-2.5 text-sm font-medium rounded-xl text-left transition-all ${
                filters.type === t.value
                  ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Guests */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Minimum Guests</label>
        <div className="flex flex-wrap gap-2">
          {guestOptions.map((g) => (
            <button
              key={`guests-filter-${g.value}`}
              onClick={() => update('guests', g.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                filters.guests === g.value
                  ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-secondary'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Price range */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Max Price per Night
        </label>
        <p className="text-sm font-bold text-primary tabular-nums mb-2">
          TZS {filters.maxPrice.toLocaleString()}
        </p>
        <input
          type="range"
          min={50000}
          max={500000}
          step={10000}
          value={filters.maxPrice}
          onChange={(e) => update('maxPrice', Number(e.target.value))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>TZS 50,000</span>
          <span>TZS 500,000</span>
        </div>
      </div>

      {/* Available only toggle */}
      <div className="flex items-center justify-between py-3 border-t border-border">
        <div>
          <p className="text-sm font-semibold text-foreground">Available Only</p>
          <p className="text-xs text-muted-foreground">Hide fully booked units</p>
        </div>
        <button
          onClick={() => update('availableOnly', !filters.availableOnly)}
          className={`relative w-11 h-6 rounded-full transition-all duration-200 ${
            filters.availableOnly ? 'bg-primary' : 'bg-muted'
          }`}
          role="switch"
          aria-checked={filters.availableOnly}
        >
          <span
            className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${
              filters.availableOnly ? 'left-6' : 'left-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
}